import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DocumentViewer from '../components/DocumentViewer';
import Dropdown from '../components/Dropdown';
import Field from '../components/Field';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import StatusBadge, { labelOf } from '../components/StatusBadge';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { isAdministration } from '../roles';

/**
 * A teacher's school, and a way to attach one.
 *
 * <p>Registration only records a School Master row when the teacher picked one from
 * the lookup. A typed name looks the same on this screen but is just text: the teacher
 * has no school, so no activation codes can reach them and no school count includes
 * them. Showing the difference — and letting a reviewer fix it here, where they are
 * already reconciling the registration against the master — is the remedy.
 */
function SchoolLink({ teacher, onLinked, onError }) {
  const [linking, setLinking] = useState(false);
  const [results, setResults] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!linking || text.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const handle = setTimeout(() => {
      api
        .listSchools({ query: text.trim(), size: 8 })
        .then((page) => setResults(page.items))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [linking, text]);

  async function link(schoolId) {
    setBusy(true);
    try {
      await api.assignTeacherSchool(teacher.teacherUserId, schoolId);
      setLinking(false);
      setText('');
      await onLinked();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  if (teacher.schoolId) {
    return <span>{teacher.schoolName}</span>;
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <span>
        {teacher.schoolName ? (
          <>
            &ldquo;{teacher.schoolName}&rdquo;{' '}
            <span className="badge badge-warn">Not linked</span>
          </>
        ) : (
          <span className="muted">Not linked</span>
        )}
      </span>
      <span className="hint" style={{ margin: 0 }}>
        Typed at registration, not chosen from the School Master. Until it is linked,
        this teacher cannot receive activation codes.
      </span>

      {!linking ? (
        <span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLinking(true)}>
            Link to a school
          </button>
        </span>
      ) : (
        <>
          <input
            className="input"
            autoFocus
            placeholder="Search the School Master"
            value={text}
            maxLength={200}
            onChange={(event) => setText(event.target.value)}
          />
          {results.length > 0 && (
            <ul className="suggestions" style={{ position: 'static' }}>
              {results.map((school) => (
                <li key={school.id}>
                  <button type="button" disabled={busy} onClick={() => link(school.id)}>
                    {school.name}
                    <span className="muted small"> · {school.code}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLinking(false)}>
              Cancel
            </button>
          </span>
        </>
      )}
    </div>
  );
}

/** Wording for each move, so the buttons read as decisions rather than enum names. */
const ACTIONS = {
  UNDER_REVIEW: { label: 'Start review', tone: 'btn-secondary' },
  ASSIGNED_TO_SALES: { label: 'Approve to sales', tone: 'btn-primary', needsRep: true },
  VERIFIED: { label: 'Verify teacher', tone: 'btn-primary' },
  REJECTED: { label: 'Reject', tone: 'btn-danger', needsRemarks: true },
  SUSPENDED: { label: 'Suspend', tone: 'btn-danger', needsRemarks: true },
  PENDING: { label: 'Return to queue', tone: 'btn-secondary' },
  EXPIRED: { label: 'Mark expired', tone: 'btn-secondary' },
};

/** A list as prose, or an em dash when there is nothing in it. */
const list = (values) => (values?.length ? values.join(', ') : '—');

/** The address on one line, skipping the parts that were left blank. */
function addressOf(teacher) {
  const parts = [teacher.area, teacher.city, teacher.state, teacher.postalCode].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

export default function TeacherReviewPage() {
  const { teacherUserId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [teacher, setTeacher] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [pending, setPending] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [repId, setRepId] = useState('');
  const [remarksError, setRemarksError] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTeacher(await api.teacherReview(teacherUserId));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [teacherUserId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // The back office reads the user list, so both of them get a rep picker. A Sales
    // Rep does not, and does not need one: they are the destination, not the router.
    if (!isAdministration(user?.role)) return;
    api
      .adminListUsers({ role: 'SALES_REP', status: 'ACTIVE', size: 100 })
      .then((result) => setSalesReps(result.items))
      .catch(() => setSalesReps([]));
  }, [user?.role]);

  function begin(status) {
    setPending(status);
    setRemarks('');
    setRepId('');
    setRemarksError(null);
  }

  async function confirm() {
    const action = ACTIONS[pending] ?? {};
    if (action.needsRemarks && !remarks.trim()) {
      setRemarksError('Give a reason — the teacher sees this on their dashboard');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await api.changeVerificationStatus(teacherUserId, {
        status: pending,
        remarks: remarks.trim() || null,
        assignedSalesRepId: repId || null,
      });
      setTeacher(updated);
      setPending(null);
      setToast(`${teacher.fullName} is now ${labelOf(pending).toLowerCase()}`);
    } catch (err) {
      setError(err);
      setPending(null);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="empty">Loading…</div>;
  if (!teacher) {
    return (
      <>
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/verification')}>
          <Icon name="back" size={15} />
          Back to the queue
        </button>
      </>
    );
  }

  const action = pending ? ACTIONS[pending] ?? {} : {};

  return (
    <>
      <div className="page-head">
        <div>
          <button type="button" className="btn btn-ghost" style={{ paddingLeft: 0, marginBottom: 8 }}
                  onClick={() => navigate('/verification')}>
            <Icon name="back" size={15} />
            Back to the queue
          </button>
          <h1>{teacher.fullName}</h1>
          <p>
            <StatusBadge status={teacher.status} />
            {teacher.schoolName ? <span className="muted"> · {teacher.schoolName}</span> : null}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--s0)', flexWrap: 'wrap' }}>
          {teacher.availableTransitions.map((status) => {
            const config = ACTIONS[status] ?? { label: labelOf(status), tone: 'btn-secondary' };
            return (
              <button key={status} type="button" className={`btn ${config.tone}`} onClick={() => begin(status)}>
                {config.label}
              </button>
            );
          })}
        </div>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {teacher.availableTransitions.length === 0 && (
        <InfoBanner>
          There is nothing for you to do here — this teacher is {labelOf(teacher.status).toLowerCase()}.
        </InfoBanner>
      )}
      {teacher.status === 'REJECTED' && teacher.rejectionReason && (
        <InfoBanner>Rejected: {teacher.rejectionReason}</InfoBanner>
      )}

      <div className="grid-2" style={{ alignItems: 'start' }}>
        <section className="card card-pad">
          <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>ID proof</h2>
          <DocumentViewer
            documentId={teacher.idProofDocumentId}
            filename={teacher.idProofFilename}
            contentType={teacher.idProofContentType}
          />
        </section>

        <div className="stack">
          <section className="card card-pad">
            <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>Registration</h2>
            <dl className="stack" style={{ margin: 0, gap: 'var(--s1)' }}>
              <Row label="Email">{teacher.email ?? '—'}</Row>
              <Row label="Mobile">{teacher.mobile}</Row>
              <Row label="School">
                <SchoolLink teacher={teacher} onLinked={load} onError={setError} />
              </Row>
              <Row label="Affiliation code">{teacher.affiliationCode ?? '—'}</Row>
              <Row label="Address">{addressOf(teacher)}</Row>
              <Row label="Designation">{teacher.designation ?? '—'}</Row>
              <Row label="Classes handling">{list(teacher.classLevels)}</Row>
              <Row label="Subjects">
                {/* The "Others" answer is shown beside the picked ones rather than
                    instead of them — a teacher may have given both. */}
                {[list(teacher.subjects), teacher.subjectOther].filter((part) => part && part !== '—')
                  .join(', ') || '—'}
              </Row>
              <Row label="Titles prescribed">{list(teacher.prescribedTitles)}</Row>
              <Row label="Assigned rep">{teacher.assignedSalesRep?.fullName ?? '—'}</Row>
              <Row label="Registered">{new Date(teacher.registeredAt).toLocaleDateString()}</Row>
              {teacher.verifiedAt && (
                <Row label="Verified">{new Date(teacher.verifiedAt).toLocaleDateString()}</Row>
              )}
              {teacher.accessExpiresAt && (
                <Row label="Access until">{new Date(teacher.accessExpiresAt).toLocaleDateString()}</Row>
              )}
            </dl>
          </section>

          <section className="card card-pad">
            <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>History</h2>
            {teacher.history.length === 0 ? (
              <p className="muted small" style={{ margin: 0 }}>Nothing has happened yet.</p>
            ) : (
              <ul className="timeline">
                {teacher.history.map((entry) => (
                  <li key={entry.id}>
                    <span className="dot-mark" />
                    <div className="timeline-body">
                      <b>
                        {entry.fromStatus ? `${labelOf(entry.fromStatus)} → ` : ''}
                        {labelOf(entry.toStatus)}
                      </b>
                      <p>
                        {entry.actorName} ({labelOf(entry.actorRole)}) ·{' '}
                        {new Date(entry.at).toLocaleString()}
                      </p>
                      {entry.remarks && <p>“{entry.remarks}”</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {pending && (
        <Modal
          title={action.label ?? labelOf(pending)}
          subtitle={`${teacher.fullName} will be notified of this change`}
          width={520}
          onClose={() => setPending(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setPending(null)}>
                Cancel
              </button>
              <button type="button" className={`btn ${action.tone ?? 'btn-primary'}`} disabled={busy}
                      onClick={confirm}>
                {busy ? 'Saving…' : action.label ?? 'Confirm'}
              </button>
            </>
          }
        >
          <div className="stack">
            {action.needsRep && salesReps.length > 0 && (
              <Field label="Sales representative" id="repId"
                     hint="Leave blank to use the school's own representative">
                <Dropdown
                  id="repId"
                  options={salesReps.map((rep) => ({ value: rep.id, label: rep.fullName }))}
                  value={repId}
                  onChange={setRepId}
                  placeholder="School's representative"
                />
              </Field>
            )}
            <Field
              label={action.needsRemarks ? 'Reason' : 'Remarks'}
              id="remarks"
              required={action.needsRemarks}
              hint={action.needsRemarks ? 'The teacher sees this' : 'Optional; recorded in the history'}
              error={remarksError}
            >
              <textarea
                id="remarks"
                className="input"
                rows={3}
                maxLength={1000}
                value={remarks}
                aria-invalid={remarksError ? 'true' : undefined}
                onChange={(event) => {
                  setRemarks(event.target.value);
                  if (remarksError) setRemarksError(null);
                }}
              />
            </Field>
          </div>
        </Modal>
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <dt className="tiny muted" style={{ letterSpacing: '.08em', textTransform: 'uppercase' }}>
        {label}
      </dt>
      <dd style={{ margin: '2px 0 0' }}>{children}</dd>
    </div>
  );
}
